import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Box, Typography, Chip, IconButton } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with webpack/vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons for different statuses
const createIcon = (color: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 5px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
};

const onlineIcon = createIcon('#10b981');
const offlineIcon = createIcon('#ef4444');
const errorIcon = createIcon('#f59e0b');
const provisioningIcon = createIcon('#6366f1');

interface Site {
  id: string;
  name: string;
  city?: string;
  latitude: number;
  longitude: number;
  organization_id: string;
  organization_name: string;
  device_count: number;
  online_count: number;
  offline_count: number;
}

interface Device {
  id: string;
  name: string;
  serial_number: string;
  status: 'online' | 'offline' | 'error' | 'provisioning';
  last_heartbeat?: string;
  organization_id: string;
  organization_name: string;
  site_id?: string;
  site_name?: string;
  latitude?: number;
  longitude?: number;
}

interface FleetMapProps {
  sites: Site[];
  devices: Device[];
  height?: string | number;
  center?: [number, number];
  zoom?: number;
  onSiteClick?: (site: Site) => void;
  onDeviceClick?: (device: Device) => void;
}

// Component to fit bounds when data changes
function FitBounds({ sites, devices }: { sites: Site[]; devices: Device[] }) {
  const map = useMap();

  useEffect(() => {
    const bounds: [number, number][] = [];

    sites.forEach(site => {
      if (site.latitude && site.longitude) {
        bounds.push([site.latitude, site.longitude]);
      }
    });

    devices.forEach(device => {
      if (device.latitude && device.longitude) {
        bounds.push([device.latitude, device.longitude]);
      }
    });

    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [map, sites, devices]);

  return null;
}

const FleetMap: React.FC<FleetMapProps> = ({
  sites,
  devices,
  height = 400,
  center = [39.8283, -98.5795], // Center of USA
  zoom = 4,
  onSiteClick,
  onDeviceClick,
}) => {
  const getDeviceIcon = (status: string) => {
    switch (status) {
      case 'online': return onlineIcon;
      case 'offline': return offlineIcon;
      case 'error': return errorIcon;
      case 'provisioning': return provisioningIcon;
      default: return offlineIcon;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return '#10b981';
      case 'offline': return '#ef4444';
      case 'error': return '#f59e0b';
      case 'provisioning': return '#6366f1';
      default: return '#6b7280';
    }
  };

  const formatLastSeen = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hr ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Filter devices that have coordinates (either from site or direct)
  const devicesWithCoords = devices.filter(d => d.latitude && d.longitude);

  return (
    <Box sx={{ height, width: '100%', borderRadius: 2, overflow: 'hidden', border: '1px solid #333' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds sites={sites} devices={devicesWithCoords} />

        {/* Site markers */}
        {sites.map((site) => (
          <Marker
            key={`site-${site.id}`}
            position={[site.latitude, site.longitude]}
            icon={site.offline_count > 0 ? offlineIcon : onlineIcon}
          >
            <Popup>
              <Box sx={{ minWidth: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {site.name}
                  </Typography>
                  {onSiteClick && (
                    <IconButton size="small" onClick={() => onSiteClick(site)}>
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {site.city || 'No city'} - {site.organization_name}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={`${site.online_count} online`}
                    size="small"
                    sx={{ bgcolor: '#10b98120', color: '#10b981' }}
                  />
                  {site.offline_count > 0 && (
                    <Chip
                      label={`${site.offline_count} offline`}
                      size="small"
                      sx={{ bgcolor: '#ef444420', color: '#ef4444' }}
                    />
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {site.device_count} device{site.device_count !== 1 ? 's' : ''} total
                </Typography>
              </Box>
            </Popup>
          </Marker>
        ))}

        {/* Device markers (for devices with direct coordinates) */}
        {devicesWithCoords.map((device) => (
          <Marker
            key={`device-${device.id}`}
            position={[device.latitude!, device.longitude!]}
            icon={getDeviceIcon(device.status)}
          >
            <Popup>
              <Box sx={{ minWidth: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {device.name}
                  </Typography>
                  {onDeviceClick && (
                    <IconButton size="small" onClick={() => onDeviceClick(device)}>
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {device.serial_number}
                </Typography>

                <Chip
                  label={device.status}
                  size="small"
                  sx={{
                    bgcolor: `${getStatusColor(device.status)}20`,
                    color: getStatusColor(device.status),
                    textTransform: 'capitalize',
                    mb: 1,
                  }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {device.site_name || 'No site'} - {device.organization_name}
                </Typography>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Last seen: {formatLastSeen(device.last_heartbeat)}
                </Typography>
              </Box>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </Box>
  );
};

export default FleetMap;
